/**
 * Authentication Service
 * Handles user authentication, token management, user registration and verification.
 */

import {
  UserRegister,
  WhoAmIResponse
} from '../api/ApiClient';
import { log } from '../utils/errors/logger';
// TokenManager removed - user caching now handled directly
import { isAppUser, mapWhoAmIToAppUser, type AppUser } from '../types/common';
// SECURITY: VULN-008 FIX - Import log sanitization utilities
import { hashIdentifier } from '../utils/security/logSanitizer';
import { getApiBasePath } from '../config/env';
// Note: ErrorHandler available but not used in current implementation

interface ApiResponse {
  detail?: string;
  exists?: boolean;
  message?: string;
}

// Note: Token management functions moved to TokenManager utility
/**
 * SECURITY: Interface for safe user data that can be stored in localStorage
 * Only non-sensitive display information
 */
class AuthService {
  // ✅ ADDED: Guard to prevent concurrent refresh attempts
  private static isRefreshing: boolean = false;

  // ✅ ADDED: Timer for proactive token refresh
  private static refreshTimer: ReturnType<typeof setTimeout> | null = null;

  // Token refresh interval: 25 minutes (5 minutes before 30-minute expiry)
  private static readonly REFRESH_INTERVAL_MS = 25 * 60 * 1000;
  // LocalStorage key for user data caching
  private static readonly USER_KEY = 'smartdoc_user';

  // SECURITY: VULN-002 FIX - Token timing configuration
  // Token configuration - should match backend settings
  private static readonly TOKEN_LIFETIME_MS = 30 * 60 * 1000; // 30 minutes
  private static readonly REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry
  private static readonly SESSION_STORAGE_KEY = 'last_token_refresh';

  /**
   * Get user data from localStorage (only non-sensitive display data)
   */
  private static getUserFromStorage(): AppUser | null {
    try {
      const userStr = localStorage.getItem(this.USER_KEY);
      if (userStr) {
        const parsed = JSON.parse(userStr) as unknown;
        return isAppUser(parsed) ? parsed : null;
      }
      return null;
    } catch (error) {
      log.error('[AuthService] Error parsing user data', error);
      return null;
    }
  }

  /**
   * SECURITY: Store ONLY non-sensitive user data to localStorage
   */
  private static setUserInStorage(user: AppUser): void {
    try {
      // Store the full AuthUser for UI access
      // Note: This is safe because AuthUser doesn't contain sensitive data like passwords
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
      log.debug('[AuthService] Stored safe user data');
    } catch (error) {
      log.error('[AuthService] Error storing user data', error);
    }
  }

  /**
   * Clear user data from localStorage
   */
  private static clearUserData(): void {
    try {
      localStorage.removeItem(this.USER_KEY);
      localStorage.removeItem('current_organization');
    } catch (error) {
      log.error('[AuthService] Error clearing user data', error);
    }
  }

  /**
   * SECURITY: VULN-002 FIX - Token timing utilities
   * Record that a token refresh occurred
   * Call this after successful login or token refresh
   */
  private static recordTokenRefresh(): void {
    const now = Date.now();
    try {
      sessionStorage.setItem(this.SESSION_STORAGE_KEY, now.toString());
      log.debug('Token refresh timestamp recorded', { timestamp: now });
    } catch (error) {
      log.warn('Failed to record token refresh timestamp', error);
    }
  }

  /**
   * Get time until token expiry in milliseconds
   * Returns null if no refresh timestamp is recorded
   */
  private static getTimeUntilExpiry(): number | null {
    try {
      const lastRefreshStr = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      if (!lastRefreshStr) {
        return null;
      }

      const lastRefresh = parseInt(lastRefreshStr, 10);
      if (isNaN(lastRefresh)) {
        return null;
      }

      const now = Date.now();
      const timeSinceRefresh = now - lastRefresh;
      const timeUntilExpiry = this.TOKEN_LIFETIME_MS - timeSinceRefresh;

      return timeUntilExpiry;
    } catch (error) {
      log.warn('Error calculating time until expiry', error);
      return null;
    }
  }

  /**
   * Check if token is expired based on recorded refresh time
   * Returns true if token is likely expired, false otherwise
   */
  private static isTokenExpired(): boolean {
    const timeUntilExpiry = this.getTimeUntilExpiry();

    if (timeUntilExpiry === null) {
      // No refresh timestamp - assume not expired (initial state)
      return false;
    }

    return timeUntilExpiry <= 0;
  }

  /**
   * Check if token should be refreshed proactively
   * Returns true if token will expire within the refresh buffer time
   * Public static method for use by apiConfig
   */
  static shouldRefreshToken(): boolean {
    const timeUntilExpiry = this.getTimeUntilExpiry();

    if (timeUntilExpiry === null) {
      // No refresh timestamp recorded - don't refresh
      return false;
    }

    // Refresh if we're within the buffer time before expiry
    return timeUntilExpiry > 0 && timeUntilExpiry < this.REFRESH_BUFFER_MS;
  }

  /**
   * Clear token refresh timestamp
   * Call this on logout or when token becomes invalid
   */
  private static clearTokenTimestamp(): void {
    try {
      sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
      log.debug('Token refresh timestamp cleared');
    } catch (error) {
      log.warn('Failed to clear token refresh timestamp', error);
    }
  }

  /**
   * Get token refresh status for debugging
   * Public static method for debugging purposes
   */
  static getTokenRefreshStatus(): {
    lastRefresh: number | null;
    timeUntilExpiry: number | null;
    isExpired: boolean;
    shouldRefresh: boolean;
  } {
    const timeUntilExpiry = this.getTimeUntilExpiry();
    const lastRefreshStr = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
    const lastRefresh = lastRefreshStr ? parseInt(lastRefreshStr, 10) : null;

    return {
      lastRefresh,
      timeUntilExpiry,
      isExpired: this.isTokenExpired(),
      shouldRefresh: this.shouldRefreshToken()
    };
  }

  /**
   * Maps WhoAmIResponse to AuthUser with backward compatibility fields
   * Adds 'id' field (alias for 'user_id') for legacy components
   */
  private static mapToAppUser(response: WhoAmIResponse): AppUser {
    return mapWhoAmIToAppUser(response);
  }

  /**
   * Start proactive token refresh timer
   * Refreshes token at 25 minutes (5 minutes before 30-minute expiry)
   * Prevents user interruption due to token expiration during active session
   */
  private static startProactiveRefresh(): void {
    // Clear any existing timer first
    this.stopProactiveRefresh();

    log.info('Starting proactive token refresh timer', {
      intervalMinutes: this.REFRESH_INTERVAL_MS / (60 * 1000)
    });

    this.refreshTimer = setTimeout(() => {
      void (async () => {
        log.info('Proactive token refresh triggered');
        const success = await this.refreshToken();

        if (success) {
          log.info('Proactive token refresh successful, restarting timer');
          // Restart timer for next refresh cycle
          this.startProactiveRefresh();
        } else {
          log.warn('Proactive token refresh failed, timer stopped');
        }
      })();
    }, this.REFRESH_INTERVAL_MS);
  }

  /**
   * Stop proactive token refresh timer
   * Called on logout or when authentication fails
   */
  private static stopProactiveRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
      log.debug('Proactive token refresh timer stopped');
    }
  }
  /**
   * Get the authentication token
   * @returns {string|null} The token or null if not logged in
   */
  static getToken(): string | null {
    log.warn('[AuthService] getToken deprecated. Tokens in HttpOnly cookies.');
    return null;
  }

  /**
   * Set the authentication token
   * @param {string} token - The authentication token
   */
  static setToken(_token: string): void {
    log.warn('[AuthService] setToken deprecated.');
  }

  /**
   * Get the refresh token
   * @returns {string|null} The refresh token or null if not available
   */
  static getRefreshToken(): string | null {
    log.warn('[AuthService] getRefreshToken deprecated.');
    return null;
  }

  /**
   * Set the refresh token
   * @param {string} refreshToken - The refresh token
   */
  static setRefreshToken(_refreshToken: string): void {
    log.warn('[AuthService] setRefreshToken deprecated.');
  }

  /**
   * Get the current user
   * @returns {Object|null} The user object or null if not logged in
   */
  static getUser(): AppUser | null {
    const user = this.getUserFromStorage();
    if (!user) { return null; }

    return user;
  }

  /**
   * Set the current user
   * @param {Object} user - The user object (WhoAmIResponse from backend)
   */
  static setUser(user: WhoAmIResponse): void {
    const appUser = this.mapToAppUser(user);
    this.setUserInStorage(appUser);
    // Dispatch event to notify components about user data change
    window.dispatchEvent(new Event('authStateChanged'));
  }

  /**
   * Log out the current user and clear all caches
   *
   * Changes (Cookie-Based Auth):
   * - Calls backend /api/auth/logout to clear HttpOnly cookies
   * - Then clears cached user data from localStorage
   */
  static async logout(): Promise<void> {
    try {
      log.info('🚪 Starting comprehensive logout process...');

      // 0. Stop proactive token refresh timer and clear session timestamp
      this.stopProactiveRefresh();
      this.clearTokenTimestamp();

      // 1. Call backend logout to clear HttpOnly cookies
      const apiUrl = getApiBasePath();
      try {
        await fetch(`${apiUrl}/auth/logout`, {
          method: 'POST',
          credentials: 'include'  // Send cookies to be cleared
        });
        log.debug('✅ Backend logout completed (cookies cleared)');
      } catch (error) {
        log.warn('Backend logout failed (continuing with client cleanup)', { error });
      }

      // 2. Clear tokens and user data
      this.clearUserData();
      log.debug('✅ Cleared tokens and user data');

      // 2. Clear TanStack Query cache (if available)
      try {
        const { queryInvalidationService } = await import('./QueryInvalidationService');
        if (queryInvalidationService) {
          queryInvalidationService.clearAllQueries();
        }
      } catch (error) {
        log.warn('Could not clear query cache', { error });
      }

      // 3. Clear browser HTTP cache for our domain
      try {
        if ('caches' in window) {
          const cacheNames = await window.caches.keys();
          const apiCaches = cacheNames.filter(name =>
            name.includes('api') || name.includes('smartdoc') || name.includes('auth')
          );
          await Promise.all(apiCaches.map((name) => window.caches.delete(name)));
          log.debug('✅ Cleared browser API caches');
        }
      } catch (error) {
        log.warn('Could not clear browser caches', { error });
      }

      // 4. Clear any Zustand stores (if they exist)
      // Note: System organization is now managed by TanStack Query (cleared above)
      // No need to clear system store separately

      try {
        // Clear conversation store by resetting UI preferences
        // Note: conversation ID is now managed via URL, not store
        const { useConversationStore } = await import('../stores/conversationStore');
        if (useConversationStore?.getState) {
          const store = useConversationStore.getState();
          store.setConversationSidebarOpen(false);
          store.setDocumentReferencesSidebarOpen(false);
          log.debug('✅ Cleared conversation store');
        }
      } catch (error) {
        log.warn('Could not clear conversation store', { error });
      }

      // 5. Clear MSAL cache for Entra auth
      try {
        const entraAuthService = await import('./EntraAuthService');
        if (entraAuthService.default?.logout) {
          await entraAuthService.default.logout();
          log.debug('✅ Cleared MSAL/Entra cache');
        }
      } catch (error) {
        log.warn('Could not clear MSAL cache', { error });
      }

      // 6. Clear localStorage items that might contain cached data
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (
            key.includes('auth') ||
            key.includes('user') ||
            key.includes('role') ||
            key.includes('smartdoc') ||
            key.startsWith('msal') ||
            key.startsWith('tanstack')
          )) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        if (keysToRemove.length > 0) {
          log.debug('✅ Cleared additional localStorage keys', { keys: keysToRemove });
        }
      } catch (error) {
        log.warn('Could not clear additional localStorage', { error });
      }

      log.info('🎉 Comprehensive logout completed successfully');

    } catch (error) {
      log.error('❌ Error during logout process', { error });
      // Even if some steps fail, ensure basic cleanup
      this.clearUserData();
    }
  }

  /**
   * Check if the user is authenticated
   *
   * Changes (Cookie-Based Auth):
   * - Calls /users/who-am-i to verify cookie validity and get user data
   * - Updates cached user data if valid
   * - No localStorage token checking (tokens in HttpOnly cookies)
   *
   * @returns {boolean} True if authenticated, false otherwise
   */
  static async checkAuthentication(): Promise<boolean> {
    log.info('🔐 AuthService.checkAuthentication() called');

    try {
      const apiUrl = getApiBasePath();

      // Call who-am-i to verify cookie validity and get user data
      const response = await fetch(`${apiUrl}/users/who-am-i`, {
        credentials: 'include'  // Send cookies
      });

      if (!response.ok) {
        log.warn('❌ Authentication failed');
        this.clearUserData();
        return false;
      }

      const userData: WhoAmIResponse = await response.json();

      // Update cached user data
      this.setUser(userData);

      // SECURITY: VULN-008 FIX - Log hashed identifier instead of username
      log.info('✅ Authentication successful', {
        userHash: hashIdentifier(userData.username),
        userId: userData.user_id,
        role: userData.role
      });

      // Record token refresh timestamp for session timeout tracking
      this.recordTokenRefresh();

      // Start proactive token refresh
      this.startProactiveRefresh();

      return true;

    } catch (error) {
      log.error('❌ Authentication check error', { error });
      this.clearUserData();
      return false;
    }
  }

  /**
   * Check if the user is authenticated
   * Cookie-based auth: Check if user data is cached (not tokens)
   * @returns {boolean} True if authenticated
   */
  static isAuthenticated(): boolean {
    return Boolean(this.getUser());
  }

  /**
   * Refresh tokens (automatic, handled by backend)
   *
   * Changes (Cookie-Based Auth):
   * - Calls /auth/refresh to update cookies (uses refresh_token cookie)
   * - Calls /users/who-am-i to get fresh user data
   * - Updates cached user data in localStorage
   * - CRITICAL: Guards against concurrent refresh attempts to prevent infinite loops
   *
   * @returns {Promise<boolean>} True if refresh successful, false otherwise
   */
  static async refreshToken(): Promise<boolean> {
    // ✅ CRITICAL: Prevent concurrent refresh attempts
    if (this.isRefreshing) {
      log.warn('Token refresh already in progress, skipping duplicate attempt');
      return false;
    }

    this.isRefreshing = true;

    try {
      const apiUrl = getApiBasePath();

      // Step 1: Refresh tokens (updates cookies)
      const refreshResponse = await fetch(`${apiUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include'  // Send refresh_token cookie
      });

      if (!refreshResponse.ok) {
        log.warn('Token refresh failed');
        this.clearUserData();
        return false;
      }

      // Step 2: Fetch updated user data
      const whoAmIResponse = await fetch(`${apiUrl}/users/who-am-i`, {
        credentials: 'include'  // Send new access_token cookie
      });

      if (!whoAmIResponse.ok) {
        log.warn('Failed to fetch user data after refresh');
        this.clearUserData();
        return false;
      }

      const userData: WhoAmIResponse = await whoAmIResponse.json();

      // Step 3: Update cached user data
      this.setUser(userData);

      log.info('Token refreshed successfully');

      // Record token refresh timestamp for session timeout tracking
      this.recordTokenRefresh();

      // Restart proactive token refresh timer
      this.startProactiveRefresh();

      return true;

    } catch (error) {
      log.error('Error refreshing token', error);
      this.clearUserData();
      return false;
    } finally {
      // ✅ CRITICAL: Always clear the refreshing flag
      this.isRefreshing = false;
    }
  }

  /**
   * Check if at least one admin user exists in the system
   * @returns {Promise<boolean>} True if an admin user exists
   */
  static async checkAdminExists(): Promise<boolean> {
    try {
      const apiUrl = getApiBasePath();
      const response = await fetch(`${apiUrl}/admin-exists`);

      if (!response.ok) {
        log.error('Error checking admin existence', { status: response.status });
        return true; // Default to true on error to hide setup info
      }

      const data: ApiResponse = await response.json();
      return data.exists || false;
    } catch (error) {
      log.error('Error checking admin existence', { error });
      return true; // Default to true on error to hide setup info
    }
  }

  /**
   * Login with username and password (cookie-based)
   *
   * Changes (Cookie-Based Auth):
   * - Step 1: Calls /auth/token to set HttpOnly cookies (backend sets cookies)
   * - Step 2: Calls /users/who-am-i to get user data (can't parse JWT from HttpOnly cookie)
   * - Step 3: Caches user data in localStorage for UI display only (NOT authentication)
   *
   * @param {string} username - Username
   * @param {string} password - Password
   * @returns {Promise<void>} No return value (user data retrieved via who-am-i)
   */
  static async login(username: string, password: string): Promise<void> {
    try {
      const apiUrl = getApiBasePath();
      log.debug('Login using API URL', { apiUrl });

      // Step 1: Login - sets HttpOnly cookies
      const loginResponse = await fetch(`${apiUrl}/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        credentials: 'include',  // ✅ Receive cookies
        body: new URLSearchParams({
          username,
          password,
        }),
      });

      if (!loginResponse.ok) {
        const errorData: ApiResponse = await loginResponse.json().catch(() => null);
        const error = new Error('Authentication failed') as Error & {
          response: { status: number; data: ApiResponse | null }
        };
        error.response = {
          status: loginResponse.status,
          data: errorData
        };
        throw error;
      }

      // Step 2: Fetch user data using the cookie
      const whoAmIResponse = await fetch(`${apiUrl}/users/who-am-i`, {
        credentials: 'include'  // ✅ Send cookie automatically
      });

      if (!whoAmIResponse.ok) {
        throw new Error('Failed to fetch user data after login');
      }

      const userData: WhoAmIResponse = await whoAmIResponse.json();

      // Step 3: Cache user data for UI (NOT for authentication)
      this.setUser(userData);

      // SECURITY: VULN-008 FIX - Log hashed identifier instead of username
      log.info('Login successful', {
        userHash: hashIdentifier(userData.username),
        userId: userData.user_id,
        role: userData.role
      });

      // Record token refresh timestamp for session timeout tracking
      this.recordTokenRefresh();

      // Start proactive token refresh timer
      this.startProactiveRefresh();

    } catch (error) {
      log.error('Login error:', { error });
      throw error;
    }
  }

  /**
   * Register a new user
   * @param {string} username - Username
   * @param {string} email - Email
   * @param {string} password - Password
   * @param {string} fullName - Full name (optional)
   * @returns {Promise<Object>} Registration result
   */
  static async register(username: string, email: string, password: string, fullName: string = ''): Promise<ApiResponse> {
    try {
      const apiUrl = getApiBasePath();
      log.debug('Register using API URL', { apiUrl });

      const registerData: UserRegister = {
        username,
        email,
        password,
        full_name: fullName || null,
      };

      const response = await fetch(`${apiUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerData),
      });

      const data: ApiResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      return data;
    } catch (error) {
      log.error('Registration error', { error });
      throw error;
    }
  }

  /**
   * Verify email with token
   * @param {string} token - Verification token
   * @returns {Promise<Object>} Verification result
   */
  static async verifyEmail(token: string): Promise<ApiResponse> {
    try {
      const apiUrl = getApiBasePath();

      const response = await fetch(`${apiUrl}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data: ApiResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Email verification failed');
      }

      return data;
    } catch (error) {
      log.error('Email verification error', { error });
      throw error;
    }
  }

  /**
   * Resend verification email
   * @param {string} email - Email address
   * @returns {Promise<Object>} Result
   */
  static async resendVerification(email: string): Promise<ApiResponse> {
    try {
      const apiUrl = getApiBasePath();
      const url = `${apiUrl}/auth/resend-verification`;
      const requestBody = { email };

      log.debug('Sending verification email request', {
        url,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: requestBody
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      log.debug('Verification email response', { status: response.status, statusText: response.statusText });

      const data: ApiResponse = await response.json();
      log.debug('Verification email response data', { data });

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend verification email');
      }

      return data;
    } catch (error) {
      log.error('Resend verification error', { error });
      throw error;
    }
  }

  /**
   * Request password reset
   * @param {string} email - Email address
   * @returns {Promise<Object>} Result
   */
  static async requestPasswordReset(email: string): Promise<ApiResponse> {
    try {
      const apiUrl = getApiBasePath();

      const response = await fetch(`${apiUrl}/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data: ApiResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to request password reset');
      }

      return data;
    } catch (error) {
      log.error('Password reset request error', { error });
      throw error;
    }
  }

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Result
   */
  static async resetPassword(token: string, newPassword: string): Promise<ApiResponse> {
    try {
      const apiUrl = getApiBasePath();

      const response = await fetch(`${apiUrl}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          new_password: newPassword,
        }),
      });

      const data: ApiResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Password reset failed');
      }

      return data;
    } catch (error) {
      log.error('Password reset error', { error });
      throw error;
    }
  }
}

export default AuthService;
